import React from 'react';
import {Platform,StyleSheet, View} from 'react-native';
import {Path,Svg} from 'react-native-svg';

import Text from '../../ui-controls/Text';

import {SvgBadgeComponent} from './types';

const BmSnsBadgeBottomImg = ({
  width,
  height,
}: {
  width: number;
  height: number;
}) => (
  <Svg width={width} height={height}>
    <Path
      d={`M0 0 L${width / 2} ${height} L${width} 0 Z`}
      fill={'rgb(237, 26, 59)'}
    />
  </Svg>
);

export const DEFAULT_BADGE_SIZE = 49;

// David made me do it
const ProportionMagicNumbers = {
  // The magic numbers are calculated based on the original badge size (49x49)
  // and the new badge size (size x size).
  ContentPadding: 0.0314,
  PriceContainer: 0.3881,
  BottomImageHeight: 0.145,
  TitleTextFont: 0.1555,
  PriceLineHeight: 0.215,
  PriceTextFont: 0.33,
  PriceDollarSignTextFont: 0.194,
  PriceDollarSignMarginTopIos: 0.1732,
  PriceDollarSignMarginTopDefault: 0.122,
};

const makeBadgeTitleTextStyles = (size: number) => {
  const badgeTitleTextLineHeight =
    size * ProportionMagicNumbers.PriceLineHeight;
  const badgeTitleTextStyles = {
    fontSize: size * ProportionMagicNumbers.TitleTextFont,
    ...Platform.select({
      // https://stackoverflow.com/questions/5703552/how-can-i-center-text-horizontally-and-vertically-inside-a-div-block
      // Grota Sans Rounded has a lot of space below the characters so the lineHeight equals
      // height/fontSize trick is not enough to center the text vertically.
      web: {
        paddingTop: 1, // hack
        height: badgeTitleTextLineHeight,
        lineHeight: badgeTitleTextLineHeight, // as same as height
      },
      ios: {
        paddingTop: 1, // hack
        height: badgeTitleTextLineHeight,
        lineHeight: badgeTitleTextLineHeight, // as same as height
      },
      android: {},
    }),
  };
  return badgeTitleTextStyles;
};

const makePriceTextStyles = (size: number) => ({
  fontSize: size * ProportionMagicNumbers.PriceTextFont,
});

const makePriceContainerStyles = (size: number) => ({
  height: size * ProportionMagicNumbers.PriceContainer,
});

const makeDollarSignTextStyles = (size: number) => ({
  fontSize: size * ProportionMagicNumbers.PriceDollarSignTextFont,
});

const makeBadgeContentStyles = (size: number) => ({
  paddingHorizontal: size * ProportionMagicNumbers.ContentPadding,
  paddingTop: size * ProportionMagicNumbers.ContentPadding,
  // hack to "fix" an annoying gap between the badge and the bottom image
  // https://teremtechnologies.slack.com/archives/C04M8027T98/p1696480984209739
  marginBottom: -0.5,
});

const BmSnsBadge: SvgBadgeComponent = ({
  priceDollars,
  priceCents,
  text,
  size = DEFAULT_BADGE_SIZE,
}) => {
  const badgeContentStyles = makeBadgeContentStyles(size);
  const badgeTitleTextStyles = makeBadgeTitleTextStyles(size);
  const priceTextStyles = makePriceTextStyles(size);
  const priceContainerStyles = makePriceContainerStyles(size);
  const dollarSignTextStyles = makeDollarSignTextStyles(size);
  const priceCentsSignTextStyles = dollarSignTextStyles;

  return (
    <View style={{width: size}} testID="badge">
      <View
        style={[styles.badgeContent, badgeContentStyles]}
        testID="badge-content">
        <View style={styles.badgeTitle} testID="badge-title">
          <View
            style={[styles.badgeTitleTextContainer]}
            testID="badge-title-text-container">
            <Text
              style={[styles.badgeTitleText, badgeTitleTextStyles]}
              testID="badge-title-text">
              {text}
            </Text>
          </View>
        </View>
        <View
          style={[styles.badgePriceContainer, priceContainerStyles]}
          testID="badge-price-container">
          <View style={styles.badgePrice} testID="badge-price">
            <View testID="badge-price-centering-view">
              <Text
                style={[styles.badgePriceDollarSignText, dollarSignTextStyles]}
                testID="badge-price-dollar-sign-text">
                $
              </Text>
            </View>
            <View>
              <Text
                style={[styles.badgePriceDollarsText, priceTextStyles]}
                testID="badge-price-dollars-text">
                {priceDollars}
              </Text>
            </View>
            <View>
              <Text
                style={[styles.badgePriceCentsText, priceCentsSignTextStyles]}
                testID="badge-price-cents-text">
                {priceCents ? '.' : ''}
                {priceCents}
              </Text>
            </View>
          </View>
        </View>
      </View>
      <BmSnsBadgeBottomImg
        width={size}
        height={size * ProportionMagicNumbers.BottomImageHeight}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  badgeContent: {
    backgroundColor: '#ED1A3B',
    display: 'flex',
    flexDirection: 'column',
  },
  badgeTitle: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    // To center vertically and horizontally the text container View/div
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeTitleTextContainer: {
    backgroundColor: '#FFFFFF',
  },
  badgeTitleText: {
    color: '#ED1A3B',
    fontWeight: '800',
    textAlignVertical: 'center', // Android only
  },
  badgePriceContainer: {
    marginTop: Platform.select({
      ios: 8,
      default: 5,
    }),
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  badgePrice: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  badgePriceDollarSignText: {
    color: '#FFFFFF',
    fontWeight: 'bold',
  },
  badgePriceDollarsText: {
    color: '#FFFFFF',
    fontWeight: '900',
    // @ts-ignore - hack for web
    lineHeight: Platform.select({
      web: '1',
    }),
  },
  badgePriceCentsText: {
    color: '#FFFFFF',
    fontWeight: '900',
  },
});

export default BmSnsBadge;
